import { UNIT_PRICE_SCALE, QUANTITY_SCALE, fromString, round, toString, COMMERCIAL_ROUNDING, } from '@aeris/money';
/**
 * Pricing.
 *
 * Master Specification section 15: "Preço é domínio separado." Not a column on
 * the product — section 12 forbids that outright — because one product has as
 * many prices as it has contexts, and the section spells the contexts out:
 * customer, quantity, branch, channel, period, payment method.
 *
 * What exists here today is the spine those hang from: a named list, and a
 * price for a variant in that list from a minimum quantity. Everything else in
 * section 15 attaches to these two without replacing them.
 *
 * Every monetary value in this file is a `Decimal` from `@aeris/money` —
 * fixed-point over `bigint`, never a JavaScript `number`. ADR-0016 explains
 * the reasoning; the short version is that `0.1 + 0.2` is
 * `0.30000000000000004`, and thirty items at R$ 0,07 is off by a centavo,
 * which is the difference between an NF-e SEFAZ authorises and one it refuses.
 *
 * This package depends on `@aeris/money` and nothing else, so the browser can
 * import the resolver and show the same price the server computed, from the
 * same code, rather than from a second implementation that agrees until it
 * does not.
 */
export const PRICING_STATUSES = ['active', 'archived'];
/**
 * ISO 4217, and only BRL is issued today.
 *
 * The column and the field exist anyway. A price without a currency is a
 * number, and adding a currency later means deciding what the rows that never
 * had one were denominated in — a guess nobody should have to make.
 *
 * There is no conversion anywhere in this feature, deliberately: a rate has a
 * source, a timestamp and a rounding rule, which is a feature rather than a
 * helper function.
 */
export const DEFAULT_CURRENCY = 'BRL';
export const isValidCurrency = (value) => /^[A-Z]{3}$/.test(value);
/**
 * Codes are typed, read aloud and printed.
 *
 * Normalising on the way in means `atacado`, ` Atacado ` and `ATACADO` are one
 * list rather than three — the difference between a uniqueness constraint that
 * protects the data and one that only appears to.
 */
export const normalizePriceListCode = (code) => code.trim().toUpperCase().replace(/\s+/g, '');
export const isValidPriceListCode = (code) => /^[A-Z0-9][A-Z0-9._/-]{0,39}$/.test(normalizePriceListCode(code));
/**
 * The machine format for a numeric column and for the wire.
 *
 * Deliberately strict, and deliberately not `parseHumanAmount`: this reads
 * what a database column or a JSON body carries, where `"1.234,56"` is not a
 * number that was typed badly, it is a protocol violation.
 */
export const MONEY_STRING = /^-?\d{1,12}(\.\d{1,6})?$/;
export const isMoneyString = (value) => MONEY_STRING.test(value.trim());
/**
 * Parses a unit price out of a database column or a request body.
 *
 * `numeric` reaches JavaScript as a *string* — the pg type parser in
 * `client.ts` makes sure of it — so this is the only conversion, and it goes
 * straight to fixed-point without passing through a `number`.
 */
export const parseUnitPrice = (value) => round(fromString(value.trim()), UNIT_PRICE_SCALE, COMMERCIAL_ROUNDING);
export const parseQuantity = (value) => round(fromString(value.trim()), QUANTITY_SCALE, COMMERCIAL_ROUNDING);
/**
 * What the wire carries: a string, always.
 *
 * Never a JSON number. Those are IEEE-754 doubles, so the exactness would be
 * lost at the boundary — including inside `JSON.parse` on a client we do not
 * control. ADR-0016 section 5.
 */
export const priceToWire = (value) => toString(value);
/** Trimmed, or null when nothing is left. A blank string is not a value. */
export const optionalText = (value) => {
    if (value === null || value === undefined)
        return null;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
};
//# sourceMappingURL=entities.js.map